import '../setupPublicPath';
import { createRoot, Root } from 'react-dom/client';
import createCache, { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { measureMount, startRender, emitRenderTiming, recordUnmount, RenderTimingProbe } from './utils/gn-performance';
import { recordGnEvent } from './utils/rum-events';
import type { StaticAttributes, PlatformRegion } from './types/Attributes';
import type { GlobalNavDisplayMode, NavVersion } from './versions/types';

class GlobalNavigatorElement extends HTMLElement {
  static tagName = 'hb-global-navigator';
  private readonly mountRoot: HTMLElement;
  private readonly emotionRoot: HTMLElement;
  private cache: EmotionCache | null = null;
  private shadow = this.attachShadow({ mode: 'open' });
  private isMounted = false;
  private staticAttributes?: StaticAttributes;
  private root: Root | null = null;

  static get observedAttributes() {
    return ['app-origin', 'nav-version'];
  }

  constructor() {
    super();
    this.mountRoot = document.createElement('div');
    this.emotionRoot = document.createElement('style');
    this.mountRoot.setAttribute('id', 'global-navigator-body');
  }

  get appData() {
    return this.staticAttributes?.appData;
  }

  set appData(value) {
    if (!this.staticAttributes) return;
    this.staticAttributes.appData = value;
    this.render();
  }

  get sessionData() {
    return this.staticAttributes?.sessionData;
  }

  set sessionData(value) {
    if (!this.staticAttributes) return;
    this.staticAttributes.sessionData = value;
    this.render();
  }

  get navVersion(): NavVersion | undefined {
    return this.staticAttributes?.navVersion;
  }

  set navVersion(value: NavVersion | undefined) {
    if (!this.staticAttributes) return;
    this.staticAttributes.navVersion = value;
    this.render();
  }

  connectedCallback() {
    this.isMounted = true;
    measureMount(() => {
      if (!this.isMounted) return;
      this.staticAttributes = {
        appOrigin: this.getAttribute('app-origin')!,
        silentRedirect: this.getAttribute('silent-redirect') === 'true',
        appId: this.getAttribute('app-id'),
        helpId: this.getAttribute('help-id'),
        isPublic: this.getAttribute('is-public') === 'true',
        orgScope: this.getAttribute('org-scope') ?? null,
        appRegion: (this.getAttribute('app-region') as PlatformRegion) ?? null,
        displayMode: this.getAttribute('display-mode')
          ? (this.getAttribute('display-mode') as GlobalNavDisplayMode)
          : 'dialog',
        excludeSessionAndGlobalNavApiCalls: this.getAttribute('exclude-session-and-global-nav-api-calls') === 'true', // when this attribute set to true, the component will not make any API calls to fetch session and global nav data, so consumers needs to pass these data via pros to appData & sessionData properties
        enableAppNav: this.getAttribute('enable-app-nav') === 'true',
        enableOidc: this.getAttribute('enable-oidc') === 'true',
        disablePendo: this.getAttribute('disable-pendo') === 'true',
        navVersion: (() => {
          const attr = this.getAttribute('nav-version');
          return attr === 'v2' || attr === 'v3' ? attr : undefined;
        })(),
      };

      this.cache = createCache({
        key: 'css',
        prepend: true,
        container: this.emotionRoot,
      });

      this.shadow.appendChild(this.emotionRoot);
      this.shadow.appendChild(this.mountRoot);
      this.root = createRoot(this.mountRoot);
      this.render();
    });
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue === newValue) return;

    if (this.staticAttributes) {
      if (name === 'app-origin') {
        this.staticAttributes.appOrigin = newValue!;
      } else if (name === 'nav-version') {
        this.staticAttributes.navVersion = newValue === 'v2' || newValue === 'v3' ? newValue : undefined;
      }

      this.render();
    }
  }

  disconnectedCallback() {
    recordUnmount();
    this.isMounted = false;
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  private render() {
    if (!this.isMounted || !this.root) return;

    const currentRenderCount = startRender();

    import('./components/AtlasGlobalNavWrapper')
      .then(({ default: AtlasGlobalNavWrapper }) => {
        if (!this.isMounted || !this.root) return;
        this.root.render(
          <CacheProvider value={this.cache!}>
            <RenderTimingProbe key={currentRenderCount} onCommit={() => emitRenderTiming(currentRenderCount)} />
            <AtlasGlobalNavWrapper container={this.mountRoot} attributes={this.staticAttributes!}>
              <slot></slot>
            </AtlasGlobalNavWrapper>
          </CacheProvider>,
        );
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[hb-global-navigator] Failed to load nav chunk:', err);
        recordGnEvent('gn.chunk_load_error', { error: message, phase: 'import' });
      });
  }
}

if (!window.customElements.get(GlobalNavigatorElement.tagName)) {
  window.customElements.define(GlobalNavigatorElement.tagName, GlobalNavigatorElement);
}
